import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../../../organisms/Form/utils";

import { InputNumeric, InputNumericProps } from "../../../../../atoms/InputNumeric";
import { usePyForm } from "../../../context";
import { FormField } from "../../FormComponents/FormField";
import { BaseFormFieldProps } from "../../FormComponents/FormField";

// Get all unique props from InputNumeric that aren't in BaseFormFieldProps
type UniqueInputProps = Omit<
	InputNumericProps,
	keyof BaseFormFieldProps<number | null | ""> | "value" | "onChange" | "onBlur"
>;

export type FormInputNumericProps = Omit<BaseFormFieldProps<number | null>, "input"> &
	UniqueInputProps & {
		allowEmpty?: boolean;
	};

/**
 * This is a form component that is used to create an input numeric field.
 * It is a wrapper around the InputNumeric component.
 * It is used to create an input numeric field that is connected to the PyForm context.
 * Its props cross-cut with the InputNumeric component and the FormField component.
 *
 * InputNumeric is an Isotope of InputBase with additional props for numeric input.
 * It is a controlled component that uses the value and onChange props to update the input value.
 */
export const FormInputNumeric = (props: FormInputNumericProps) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	return (
		<FormField<number | null>
			{...inputWrapperProps}
			input={(field) => {
				const numericProps = {
					...inputProps,
					onBlur: field.onBlur,
					id: inputWrapperProps.id,
					disabled: inputWrapperProps.disabled,
				};

				if (props.allowEmpty) {
					return (
						<InputNumeric
							{...numericProps}
							value={field.value}
							data-testid={`${inputWrapperProps["data-testid"]}-numeric`}
							onChange={field.onChange}
							allowEmpty={true}
						/>
					);
				}

				return (
					<InputNumeric
						{...numericProps}
						value={typeof field.value === "string" ? 0 : (field.value ?? 0)}
						onChange={(value) => field.onChange(value)}
						allowEmpty={false}
					/>
				);
			}}
		/>
	);
};

export default FormInputNumeric;
